//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import equal from 'fast-deep-equal';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  CuralateCatalogProduct,
  TrackingParamGroup,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import { Alert } from '@components/atoms/alert/Alert';
import {
  InvisibleButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import EditableName from '@components/atoms/editable-name/EditableName';
import { SubsectionText } from '@components/atoms/text/Text';
import { Upload } from '@components/molecules/upload/Upload';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import FileId from '@models/FileId';
import { Product, UiaProductTagRequest } from '@models/IItemMetadata';
import { Mixins } from '@theme/mixins';
import {
  CRAWL_PRODUCT_TYPES,
  UiaProductTrackingIds,
} from '@uia/products/constants/UiaProductConstants';
import { useProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { ProductImageCarousel } from '@uia/products/editor/curalate-product-editor/components/ProductImageCarousel';
import { ProductLoadingError } from '@uia/products/editor/curalate-product-editor/components/ProductLoadingError';
import { ProductPreviewUrlAndEditCta } from '@uia/products/editor/curalate-product-editor/components/ProductPreviewUrlAndEditCta';
import { UrlNotFoundWarning } from '@uia/products/editor/curalate-product-editor/components/UrlNotFoundWarning';
import { UrlTrackingParamsEditor } from '@uia/products/editor/curalate-product-editor/components/UrlTrackingParamsEditor';
import { useTrackingParamsByVersionQuery } from '@uia/products/hooks/useCurrentTrackingParamsQuery';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { useUploadCatalogProductImageMutation } from '@uia/products/hooks/useUploadCatalogProductImageMutation';
import { getIsUrlTrackingParamsEnabled } from '@uia/products/selectors/getIsUrlTrackingParamsEnabled';
import {
  getProductOrTagImageUrls,
  getTrackingSettings,
  mergeTrackingParamsWithOverrides,
} from '@uia/products/utils';
import { isDefined, isUndefined } from '@utils/filterPredicates';

interface Props {
  onClose: () => void;
  product: Product | CuralateCatalogProduct;
  trackingParamsVersion: number;
}

export const ProductEditorDetails = ({
  onClose,
  product,
  trackingParamsVersion,
}: Props) => {
  const dispatch = useDispatch();
  const {
    addProduct,
    editProduct,
    isProductEditingFeatureDisabled,
    metadata,
    setIsTaggingProducts,
    setSearchQuery,
    setSelectedProductToEdit,
  } = useProductTaggingContext();
  const isTrackingParamsEnabled = useSelector(getIsUrlTrackingParamsEnabled);
  const isCrawledProduct = CRAWL_PRODUCT_TYPES.includes(
    product.productType ?? '',
  );
  const isEditingProduct = metadata.productTags.some(
    (tag) => tag.productStyleId === product.productStyleId,
  );

  const [productName, setProductName] = useState(product.name);
  const [isEditingUrlAndTrackingParams, setIsEditingUrlAndTrackingParams] =
    useState(false);
  const [overrideParamGroups, setOverrideParamGroups] = useState<
    TrackingParamGroup[] | undefined
  >(undefined);
  const [draftDestinationUrl, setDraftDestinationUrl] = useState<
    string | undefined
  >(undefined);

  const { data: catalogs } = useProductCatalogsQuery();
  const isCatalogConnected = isDefined(catalogs) && catalogs.length > 0;

  const {
    data: trackingParametersResponse,
    isError,
    isLoading,
  } = useTrackingParamsByVersionQuery(trackingParamsVersion);

  const existingProductImage = getProductOrTagImageUrls(product)[0];
  const {
    data: uploadedImage,
    isLoading: isUploading,
    mutateAsync: uploadProductImageAsync,
  } = useUploadCatalogProductImageMutation();

  const handleUploadImage = async (files: FileList | null) => {
    if (files?.[0]) {
      try {
        await uploadProductImageAsync(files[0]);
      } catch {
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Something went wrong',
            message:
              'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
            type: ToastType.ERROR,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    }
  };

  const shouldDisableTagUpdateButton =
    isLoading || isUploading || metadata.disableProductTagging;

  const handleSaveProduct = () => {
    const originalTrackingParamGroups =
      trackingParametersResponse?.trackingParams?.paramGroups.filter(
        (group) => group.inUse,
      );

    if (!originalTrackingParamGroups || !product.productStyleId) {
      return;
    }

    const position =
      isEditingProduct && 'position' in product
        ? product.position
        : metadata.productTags.length + 1;

    const productWithUpdates: UiaProductTagRequest = {
      ...product,
      ...uploadedImage,
      imageUrl: uploadedImage?.imageUrl ?? existingProductImage,
      name: productName,
      destinationUrl: draftDestinationUrl ?? product.destinationUrl,
      productStyleId: product.productStyleId,
      position,
    };

    // used to compare if the product has been updated or not
    const productWithoutUpdates: UiaProductTagRequest = {
      ...product,
      imageUrl: existingProductImage,
      productStyleId: product.productStyleId,
      position,
    };

    const trackingSettings = getTrackingSettings({
      isTrackingParamsEnabled:
        isTrackingParamsEnabled && !isProductEditingFeatureDisabled,
      originalTrackingParams: {
        paramGroups: originalTrackingParamGroups,
      },
      overrideTrackingParams: {
        paramGroups: overrideParamGroups ?? [],
      },
      trackingParamsVersion,
      product: productWithUpdates,
    });

    const finalProduct = {
      ...productWithUpdates,
      ...trackingSettings,
      isUpdated: isEditingProduct,
    };

    if (finalProduct.isUpdated) {
      const existingProduct = metadata.productTags.find(
        (p) => finalProduct.productStyleId === p.productStyleId,
      );

      if (existingProduct) {
        editProduct({
          updatedProduct: {
            ...existingProduct,
            ...finalProduct,
            fileId: finalProduct.fileId
              ? FileId(finalProduct.fileId)
              : existingProduct.fileId,
          },
          productList: metadata.productTags,
        });
      }
    } else {
      addProduct(
        {
          ...finalProduct,
          // When adding a new crawled product, you can edit some of the details before tagging it.
          // If we have changed the product details before tagging it then we need to set the isUpdated flag
          isUpdated:
            !equal(productWithoutUpdates, productWithUpdates) ||
            isDefined(overrideParamGroups),
        },
        metadata.productTags,
      );
    }

    setIsTaggingProducts(false);
    setSelectedProductToEdit(undefined);
    setSearchQuery('');
    onClose();
  };

  const baseParamGroups =
    trackingParametersResponse?.trackingParams?.paramGroups ?? [];

  const mergedTrackingSettingsAndProductOverrides =
    mergeTrackingParamsWithOverrides(
      baseParamGroups,
      'trackingParamsData' in product ? product.trackingParamsData : undefined,
    );

  const mergedEditorOverrides = overrideParamGroups
    ? mergeTrackingParamsWithOverrides(
        mergedTrackingSettingsAndProductOverrides,
        JSON.stringify({
          overrides: overrideParamGroups.flatMap((group) => group.params),
        }),
      )
    : mergedTrackingSettingsAndProductOverrides;

  if (isEditingUrlAndTrackingParams) {
    return (
      <>
        {!isLoading &&
        (isError || isUndefined(trackingParametersResponse.trackingParams)) ? (
          <ProductLoadingError />
        ) : (
          <UrlTrackingParamsEditor
            isProductEditingFeatureDisabled={isProductEditingFeatureDisabled}
            onClose={() => setIsEditingUrlAndTrackingParams(false)}
            onUpdateUrl={({
              destinationUrl,
              paramGroups,
            }: {
              destinationUrl: string;
              paramGroups: TrackingParamGroup[];
            }) => {
              setOverrideParamGroups(paramGroups);
              setDraftDestinationUrl(destinationUrl);
              setIsEditingUrlAndTrackingParams(false);
            }}
            paramGroups={mergedEditorOverrides}
            product={product}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isCatalogConnected && isCrawledProduct && <UrlNotFoundWarning />}

      <EditorContainer>
        <EditableName
          name={productName ?? ''}
          onSaveName={async (name) => {
            // Wrap the state setter in a Promise because EditableName expects a fn with a return type of Promise<void>
            await new Promise<void>((resolve) => {
              setProductName(name);
              resolve();
            });
          }}
          placeholder='Name not found'
          showEditIcon={false}
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_EDIT_PRODUCT_NAME}
        />
        <BodyContainer>
          <ProductImageCarousel
            product={product}
            uploadedImage={uploadedImage}
          />
          <ProductInfo>
            <SubsectionText>Product URL</SubsectionText>
            {!isLoading && isError && (
              <Alert
                trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_ERROR_ALERT}
                type='error'
              >
                We had a problem loading your tracking parameters. Please try
                again later or contact Bazaarvoice support to edit this product
                tag URL.
              </Alert>
            )}
            <ProductPreviewUrlAndEditCta
              overrideParamGroups={mergedEditorOverrides.flatMap(
                (group) => group.params,
              )}
              productUrl={draftDestinationUrl ?? product.destinationUrl}
              setIsEditing={() => setIsEditingUrlAndTrackingParams(true)}
              trackingParamsVersion={trackingParamsVersion}
            />
          </ProductInfo>
        </BodyContainer>
        <ProductActions>
          <Upload
            buttonText='Upload Image'
            handleChange={handleUploadImage}
            isDisabled={isUploading}
            shouldAcceptMultipleFiles={false}
            trackingId={
              UiaProductTrackingIds.PRODUCT_EDITOR_UPLOAD_PRODUCT_IMAGE
            }
          />
          <ButtonsContainer>
            <InvisibleButton
              onClick={onClose}
              trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_CANCEL_BUTTON}
            >
              Cancel
            </InvisibleButton>
            <PrimaryButton
              isDisabled={shouldDisableTagUpdateButton}
              onClick={handleSaveProduct}
              trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_SAVE_BUTTON}
            >
              {isEditingProduct ? 'Update' : 'Tag'}
            </PrimaryButton>
          </ButtonsContainer>
        </ProductActions>
      </EditorContainer>
    </>
  );
};

const EditorContainer = styled.div(
  ({ theme }) => `
    ${Mixins.Sheet.Low}
    flex-direction: column;
    gap: ${theme.spacings[2]};
    padding: ${theme.spacings[2]};
  `,
);

const ButtonsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[3]};
  `,
);

const ProductInfo = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: ${theme.spacings[1]};
  `,
);

const ProductActions = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding-top: ${theme.spacings[2]};
  `,
);

const BodyContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${theme.spacings[3]};
  `,
);
